"use client";

import { useState } from "react";
import CommonSchema from "@/components/common/CommonSchema";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { motion } from "motion/react";
import {
  ChevronLeft,
  Utensils,
  Clock,
  MapPin,
  User,
  Calendar,
  Users as UsersIcon,
  MessageSquare,
  Mail,
  Phone,
} from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/navigation";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import { toast } from "sonner";
import { insertInquiry } from "@/services/home.services";
import { cleanHeaderTags, isValidImgSrc } from "@/_helper/helper";
import { Banner } from "../common/Banner";
import { useInquiryForm } from "@/hooks/useInquiryForm";
import * as Yup from "yup";

export default function DiningClient({ diningData, initialBanner }: { diningData: any[]; initialBanner?: any }) {
  const router = useRouter();
  const dispatch = useAppDispatch();

  const BannerList = useAppSelector((state) => state.home.BannerList);
  const banner: any =
    initialBanner ||
    BannerList?.find((banner: any) => banner?.BannerType === "dining") || {};

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentGallery, setCurrentGallery] = useState<any[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    time: "",
    guests: "",
    venue: "",
    message: "",
  });

  const diningValidationSchema = Yup.object({
    FirstName: Yup.string().required("Name is required"),
    MobileNo: Yup.string()
      .matches(/^[0-9+\s-]{10,15}$/, "Invalid phone number")
      .required("Phone number is required"),
    Email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    CheckIn: Yup.date()
      .required("Expected travel date is required")
      .min(
        new Date(new Date().setHours(0, 0, 0, 0)),
        "Date cannot be in the past",
      ),
    CheckInTime: Yup.string().required("Time is required"),
    CheckOut: Yup.date().nullable(),
    Adults: Yup.number()
      .typeError("Must be a number")
      .required("Number of guests is required")
      .min(1, "At least 1 guest is required")
      .max(100, "Maximum 100 guests allowed"),
    Remarks: Yup.string().required("Message is required"),
  });

  const { formik, submitted } = useInquiryForm(
    "Dining Booking",
    diningValidationSchema,
  );

  const openLightbox = (images: string[], index: number) => {
    setCurrentGallery(images.map((src) => ({ src })));
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await dispatch(
        insertInquiry({
          Name: formData.name,
          Phone: formData.phone,
          Email: formData.email,
          Message: `Date: ${formData.date}, Time: ${formData.time}, Guests: ${formData.guests}, Venue: ${formData.venue}. Message: ${formData.message}`,
          Type: "Dining",
        }),
      ).unwrap();

      toast.success("Dining inquiry submitted successfully!");
      setTimeout(() => {
        router.push("/thank-you");
      }, 2000);
    } catch (error: any) {
      toast.error(error.message || "Failed to submit inquiry");
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen bg-[#fdfcfa]">
      <CommonSchema />
      {/* Hero Section */}
      {/* <section className="pt-40 pb-16 bg-gradient-to-br from-[#5a6f3e] via-[#4a5f2e] to-[#5a6f3e] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <Image
            src="https://images.unsplash.com/photo-1598147531881-c8c8067b6a80?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=1920"
            alt="Pattern"
            fill
            className="object-cover"
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link
              href="/"
              className="inline-flex items-center gap-2 text-[#d4af37] mb-6 hover:gap-3 transition-all"
            >
              <ChevronLeft className="w-5 h-5" />
              <span style={{ fontFamily: "Jost, sans-serif" }}>
                Back to Home
              </span>
            </Link>

            <div className="flex items-center gap-4 mb-6">
              <Utensils className="w-16 h-16 text-[#d4af37]" />
            </div>

            <h1
              className="text-5xl md:text-6xl lg:text-7xl mb-6 leading-tight"
              style={{
                fontFamily: "Cormorant Garamond, serif",
                fontWeight: 400,
              }}
            >
              Culinary{" "}
              <span style={{ fontWeight: 600, fontStyle: "italic" }}>
                Excellence
              </span>
            </h1>
            <p
              className="text-xl text-white/90 max-w-3xl leading-relaxed"
              style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
            >
              Embark on a gastronomic journey through our diverse dining venues,
              each offering unique ambiance and exceptional cuisine crafted by
              master chefs.
            </p>
          </motion.div>
        </div>
      </section> */}
      <Banner
        title={banner?.BannerTitle || "Fine Dining & Culinary Excellence"}
        description={banner?.Description}
        bgImage={banner?.BannerImage}
        // icon={<Utensils className="w-16 h-16 text-[#d4af37]" />}
      />

      {/* Dining Venues */}
      <section className="py-20 bg-[#fdfcfa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="space-y-24">
            {diningData?.map((venue: any, index: number) => {
              const GalleryImages = [
                venue?.Images,
                ...venue?.DiningInnerImage?.map(
                  (img: any) => img?.ImageGallery,
                ),
              ].filter(Boolean);

              return (
                <motion.article
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  id={`venue-${index}`}
                  className="scroll-mt-24"
                >
                  {/* Venue Header */}
                  <div className="text-center mb-12">
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      viewport={{ once: true }}
                      className="inline-block mb-4"
                    >
                      <div className="w-16 h-16 bg-[#d4af37] rounded-full flex items-center justify-center mx-auto">
                        <span
                          className="text-3xl text-white"
                          style={{
                            fontFamily: "Cormorant Garamond, serif",
                            fontWeight: 600,
                          }}
                        >
                          {index + 1}
                        </span>
                      </div>
                    </motion.div>

                    <h2
                      className="text-4xl md:text-5xl text-[#2a2520] mb-3"
                      style={{
                        fontFamily: "Cormorant Garamond, serif",
                        fontWeight: 600,
                      }}
                    >
                      {venue?.DiningTitle}
                    </h2>
                    <p
                      className="text-xl text-[#d4af37]"
                      style={{
                        fontFamily: "Jost, sans-serif",
                        fontStyle: "italic",
                      }}
                    >
                      {venue?.DiningTagline}
                    </p>
                  </div>

                  <div className="bg-white shadow-2xl overflow-hidden border border-[#5a6f3e]/10">
                    {/* Image Gallery */}
                    <div className="grid md:grid-cols-4 gap-2 p-2">
                      {GalleryImages?.slice(0, 5).map((img: any, i: number) => (
                        <motion.div
                          key={i}
                          // whileHover={{ scale: 1.05, zIndex: 10 }}
                          className={`relative overflow-hidden group cursor-pointer ${i === 0 ? "md:col-span-2 md:row-span-2 min-h-96 h-full" : "h-48"}`}
                          onClick={() => openLightbox(GalleryImages, i)}
                        >
                          <Image
                            src={img}
                            alt={`${venue?.DiningTitle} ${i + 1}`}
                            fill
                            className="object-cover group-hover:scale-110 transition-all duration-700 ease-in-out"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 hover:opacity-100 transition-opacity" />
                          {i === 4 && GalleryImages.length > 5 && (
                            <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center backdrop-blur-[2px]">
                              <span
                                className="text-white text-3xl font-bold"
                                style={{ fontFamily: "Jost, sans-serif" }}
                              >
                                +{GalleryImages.length - 5}
                              </span>
                              <span
                                className="text-white/80 text-sm font-medium uppercase tracking-wider"
                                style={{ fontFamily: "Jost, sans-serif" }}
                              >
                                More Photos
                              </span>
                            </div>
                          )}
                        </motion.div>
                      ))}
                    </div>

                    {/* Venue Details */}
                    <div className="p-8 md:p-12">
                      <div className="grid md:grid-cols-3 gap-6 mb-8 pb-8 border-b border-[#5a6f3e]/10">
                        {venue?.Timing && (
                          <div className="flex items-start gap-3">
                            <Clock className="w-6 h-6 text-[#5a6f3e] flex-shrink-0 mt-1" />
                            <div>
                              <p
                                className="text-sm text-[#2a2520]/60 mb-1"
                                style={{ fontFamily: "Jost, sans-serif" }}
                              >
                                Timing
                              </p>
                              <p
                                className="font-semibold text-[#2a2520]"
                                style={{ fontFamily: "Jost, sans-serif" }}
                              >
                                {venue?.Timing}
                              </p>
                            </div>
                          </div>
                        )}

                        {venue?.Seating ? (
                          <div className="flex items-start gap-3">
                            <UsersIcon className="w-6 h-6 text-[#5a6f3e] flex-shrink-0 mt-1" />
                            <div>
                              <p
                                className="text-sm text-[#2a2520]/60 mb-1"
                                style={{ fontFamily: "Jost, sans-serif" }}
                              >
                                Seating
                              </p>
                              <p
                                className="font-semibold text-[#2a2520]"
                                style={{ fontFamily: "Jost, sans-serif" }}
                              >
                                {venue?.Seating || 0}
                              </p>
                            </div>
                          </div>
                        ) : null}

                        {venue?.Cuisine && (
                          <div className="flex items-start gap-3">
                            <Utensils className="w-6 h-6 text-[#5a6f3e] flex-shrink-0 mt-1" />
                            <div>
                              <p
                                className="text-sm text-[#2a2520]/60 mb-1"
                                style={{ fontFamily: "Jost, sans-serif" }}
                              >
                                Cuisine
                              </p>
                              <p
                                className="font-semibold text-[#2a2520]"
                                style={{ fontFamily: "Jost, sans-serif" }}
                              >
                                {venue?.Cuisine}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="mb-8">
                        <div
                          className="text-lg text-[#2a2520] mb-4 leading-relaxed"
                          style={{
                            fontFamily: "Jost, sans-serif",
                            fontWeight: 300,
                          }}
                          dangerouslySetInnerHTML={{
                            __html: cleanHeaderTags(venue?.DiningDescription),
                          }}
                        />
                        {/* {venue?.fullDescription &&
                          venue?.fullDescription !== venue?.description && (
                            <div
                              className="text-[#2a2520]/70 leading-relaxed"
                              style={{
                                fontFamily: "Jost, sans-serif",
                                fontWeight: 300,
                              }}
                              dangerouslySetInnerHTML={{
                                __html: venue?.fullDescription,
                              }}
                            />
                          )} */}
                      </div>

                      <div className="mb-8">
                        <h3
                          className="text-xl mb-4 text-[#2a2520]"
                          style={{
                            fontFamily: "Cormorant Garamond, serif",
                            fontWeight: 600,
                          }}
                        >
                          Specialties
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {venue?.DiningSpecialities?.map(
                            (specialty: any, i: number) => (
                              <span
                                key={i}
                                className="bg-[#5a6f3e]/10 text-[#5a6f3e] px-4 py-2 text-sm border border-[#5a6f3e]/20"
                                style={{ fontFamily: "Jost, sans-serif" }}
                              >
                                {specialty?.DiningSpecialities}
                              </span>
                            ),
                          )}
                        </div>
                      </div>

                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                          const form = document.getElementById("inquiry-form");
                          if (form) {
                            form.scrollIntoView({ behavior: "smooth" });
                            setFormData({
                              ...formData,
                              venue: venue?.DiningTitle,
                            });
                          }
                        }}
                        className="bg-[#5a6f3e] text-white px-8 py-3 hover:bg-[#4a5f2e] transition-colors shadow-lg cursor-pointer"
                        style={{
                          fontFamily: "Jost, sans-serif",
                          fontWeight: 600,
                        }}
                      >
                        Make a Reservation
                      </motion.button>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section
        id="inquiry-form"
        className="py-20 bg-gradient-to-br from-[#f5f1ec] to-[#fdfcfa] mb-20"
      >
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-4xl md:text-5xl text-[#2a2520] mb-4"
              style={{
                fontFamily: "Cormorant Garamond, serif",
                fontWeight: 600,
              }}
            >
              Dining Inquiry
            </h2>
            <p
              className="text-lg text-[#2a2520]/70"
              style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
            >
              Reserve your table or inquire about our dining experiences
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-white shadow-2xl p-8 md:p-12 border border-[#5a6f3e]/10"
          >
            <form onSubmit={formik.handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="FirstName"
                    className="block text-[#2a2520] mb-2 font-medium"
                    style={{ fontFamily: "Jost, sans-serif" }}
                  >
                    Full Name *
                  </label>
                  <div
                    className={`flex items-center gap-2 border border-[#5a6f3e]/30 px-4 py-3 bg-[#f5f1ec]/30 focus-within:border-[#5a6f3e] transition-colors ${formik.touched.FirstName && formik.errors.FirstName ? "border-red-500" : ""}`}
                  >
                    <User className="w-5 h-5 text-[#5a6f3e]" />
                    <input
                      type="text"
                      id="FirstName"
                      {...formik.getFieldProps("FirstName")}
                      className="bg-transparent outline-none w-full"
                      style={{ fontFamily: "Jost, sans-serif" }}
                      placeholder="Enter your name"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="Email"
                    className="block text-[#2a2520] mb-2 font-medium"
                    style={{ fontFamily: "Jost, sans-serif" }}
                  >
                    Email Address *
                  </label>
                  <div
                    className={`flex items-center gap-2 border border-[#5a6f3e]/30 px-4 py-3 bg-[#f5f1ec]/30 focus-within:border-[#5a6f3e] transition-colors ${formik.touched.Email && formik.errors.Email ? "border-red-500" : ""}`}
                  >
                    <Mail className="w-5 h-5 text-[#5a6f3e]" />
                    <input
                      type="email"
                      id="Email"
                      {...formik.getFieldProps("Email")}
                      className="bg-transparent outline-none w-full"
                      style={{ fontFamily: "Jost, sans-serif" }}
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="MobileNo"
                    className="block text-[#2a2520] mb-2 font-medium"
                    style={{ fontFamily: "Jost, sans-serif" }}
                  >
                    Phone Number *
                  </label>
                  <div
                    className={`flex items-center gap-2 border border-[#5a6f3e]/30 px-4 py-3 bg-[#f5f1ec]/30 focus-within:border-[#5a6f3e] transition-colors ${formik.touched.MobileNo && formik.errors.MobileNo ? "border-red-500" : ""}`}
                  >
                    <Phone className="w-5 h-5 text-[#5a6f3e]" />
                    <input
                      type="tel"
                      id="MobileNo"
                      {...formik.getFieldProps("MobileNo")}
                      className="bg-transparent outline-none w-full"
                      style={{ fontFamily: "Jost, sans-serif" }}
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="CheckIn"
                    className="block text-[#2a2520] mb-2 font-medium"
                    style={{ fontFamily: "Jost, sans-serif" }}
                  >
                    Preferred Date *
                  </label>
                  <div
                    className={`flex items-center gap-2 border border-[#5a6f3e]/30 px-4 py-3 bg-[#f5f1ec]/30 focus-within:border-[#5a6f3e] transition-colors ${formik.touched.CheckIn && formik.errors.CheckIn ? "border-red-500" : ""}`}
                  >
                    <Calendar className="w-5 h-5 text-[#5a6f3e]" />
                    <input
                      type="date"
                      id="CheckIn"
                      {...formik.getFieldProps("CheckIn")}
                      onClick={(e) => (e.currentTarget as any).showPicker?.()}
                      className="bg-transparent outline-none w-full cursor-pointer"
                      style={{ fontFamily: "Jost, sans-serif" }}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="CheckInTime"
                    className="block text-[#2a2520] mb-2 font-medium"
                    style={{ fontFamily: "Jost, sans-serif" }}
                  >
                    Preferred Time *
                  </label>
                  <div
                    className={`flex items-center gap-2 border border-[#5a6f3e]/30 px-4 py-3 bg-[#f5f1ec]/30 focus-within:border-[#5a6f3e] transition-colors ${formik.touched.CheckInTime && formik.errors.CheckInTime ? "border-red-500" : ""}`}
                  >
                    <Clock className="w-5 h-5 text-[#5a6f3e]" />
                    <input
                      type="time"
                      id="CheckInTime"
                      {...formik.getFieldProps("CheckInTime")}
                      onClick={(e) => (e.currentTarget as any).showPicker?.()}
                      className="bg-transparent outline-none w-full cursor-pointer"
                      style={{ fontFamily: "Jost, sans-serif" }}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="Adults"
                    className="block text-[#2a2520] mb-2 font-medium"
                    style={{ fontFamily: "Jost, sans-serif" }}
                  >
                    Number of Guests *
                  </label>
                  <div
                    className={`flex items-center gap-2 border border-[#5a6f3e]/30 px-4 py-3 bg-[#f5f1ec]/30 focus-within:border-[#5a6f3e] transition-colors ${formik.touched.Adults && formik.errors.Adults ? "border-red-500" : ""}`}
                  >
                    <UsersIcon className="w-5 h-5 text-[#5a6f3e]" />
                    <input
                      type="number"
                      id="Adults"
                      max={100}
                      {...formik.getFieldProps("Adults")}
                      className="bg-transparent outline-none w-full"
                      style={{ fontFamily: "Jost, sans-serif" }}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label
                  htmlFor="DiningID"
                  className="block text-[#2a2520] mb-2 font-medium"
                  style={{ fontFamily: "Jost, sans-serif" }}
                >
                  Dining Venue *
                </label>
                <div
                  className={`flex items-center gap-2 border border-[#5a6f3e]/30 px-4 py-3 bg-[#f5f1ec]/30 focus-within:border-[#5a6f3e] transition-colors ${formik.touched.DiningID && formik.errors.DiningID ? "border-red-500" : ""}`}
                >
                  <MapPin className="w-5 h-5 text-[#5a6f3e]" />
                  <select
                    id="DiningID"
                    {...formik.getFieldProps("DiningID")}
                    className="bg-transparent outline-none w-full cursor-pointer"
                    style={{ fontFamily: "Jost, sans-serif" }}
                  >
                    <option value="">Select a venue</option>
                    {diningData?.map((venue: any, index: number) => (
                      <option key={index} value={venue?.ID}>
                        {venue?.DiningTitle}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label
                  htmlFor="Remarks"
                  className="block text-[#2a2520] mb-2 font-medium"
                  style={{ fontFamily: "Jost, sans-serif" }}
                >
                  Special Requests / Message
                </label>
                <div className="flex items-start gap-2 border border-[#5a6f3e]/30 px-4 py-3 bg-[#f5f1ec]/30 focus-within:border-[#5a6f3e] transition-colors">
                  <MessageSquare className="w-5 h-5 text-[#5a6f3e] mt-1" />
                  <textarea
                    id="Remarks"
                    {...formik.getFieldProps("Remarks")}
                    rows={4}
                    className="bg-transparent outline-none w-full resize-none"
                    style={{ fontFamily: "Jost, sans-serif" }}
                    placeholder="Any dietary restrictions, special occasions, or specific requirements..."
                  />
                </div>
              </div>

              <motion.button
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={formik.isSubmitting}
                className="w-full bg-[#5a6f3e] text-white py-4 px-8 hover:bg-[#4a5f2e] transition-colors shadow-lg cursor-pointer"
                style={{
                  fontFamily: "Jost, sans-serif",
                  fontWeight: 600,
                  fontSize: "1.125rem",
                }}
              >
                Submit Inquiry
              </motion.button>

              <p
                className="text-center text-sm text-[#2a2520]/60"
                style={{ fontFamily: "Jost, sans-serif" }}
              >
                Our team will contact you within 24 hours to confirm your
                reservation
              </p>
            </form>
          </motion.div>
        </div>
      </section>

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={currentGallery}
        index={currentIndex}
        styles={{ slide: { padding: 0 } }}
        render={{
          slide: ({ slide }) => (
            <div className="relative w-[90%] h-full flex items-center justify-center">
              <Image
                src={slide.src || ""}
                alt={"Gallery Image " + (currentIndex + 1)}
                fill
                sizes="(max-width: 768px) 100vw, 90vw"
                className="object-contain pointer-events-none"
              />
            </div>
          ),
        }}
      />
    </div>
  );
}
